// Free, keyless Google Maps deep links (no API key, no Google Cloud account
// needed): https://developers.google.com/maps/documentation/urls/get-started
export function directionsUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
}

// Free CARTO basemap tiles (OpenStreetMap data), no API key. Commercial-use
// friendly tier, unlike the bare tile.openstreetmap.org server which is
// evaluation-only per OSMF policy.
export const TILE_URL = "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png";
export const TILE_ATTRIBUTION =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';

// A static map, assembled by the browser out of plain <img> tags.
//
// The club page used to load Leaflet for this: a 148 kB chunk, a JavaScript
// runtime and a stacking context that fought the sticky header, all so someone
// could pan around a road map they never pan around. A detail page needs one
// answer, "where is this", and that is a picture.
//
// So: no library. Work out which slippy-map tiles cover the window, position
// them with CSS, and drop a pin at the point. Same free CARTO tiles as before,
// four image requests, no script, and it renders server-side so it is in the
// HTML rather than appearing a beat later.
const TILE_SIZE = 256;

export type StaticTile = { url: string; left: number; top: number };
export type StaticMap = {
  tiles: StaticTile[];
  /** Pin position within the window, in pixels from the top left. */
  pin: { left: number; top: number };
  width: number;
  height: number;
};

/** Web Mercator: longitude and latitude to absolute pixels at a given zoom. */
function project(lat: number, lng: number, zoom: number): { x: number; y: number } {
  const scale = TILE_SIZE * 2 ** zoom;
  const sin = Math.sin((lat * Math.PI) / 180);
  return {
    x: scale * (lng / 360 + 0.5),
    y: scale * (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)),
  };
}

export function staticMap(
  lat: number,
  lng: number,
  { width = 640, height = 400, zoom = 15 } = {},
): StaticMap {
  const centre = project(lat, lng, zoom);
  // Top-left corner of the window in world pixels.
  const originX = centre.x - width / 2;
  const originY = centre.y - height / 2;

  // Only the tiles the window actually touches. A window narrower than two
  // tiles can straddle at most two tile boundaries per axis, so this is four
  // images in the common case and never more than six.
  const max = 2 ** zoom;
  const tiles: StaticTile[] = [];
  for (let tx = Math.floor(originX / TILE_SIZE); tx <= Math.floor((originX + width) / TILE_SIZE); tx++) {
    for (let ty = Math.floor(originY / TILE_SIZE); ty <= Math.floor((originY + height) / TILE_SIZE); ty++) {
      // Wrap east-west at the date line; clamp north-south, where there is no
      // wrap and an out-of-range row would 404.
      if (ty < 0 || ty >= max) continue;
      const wrappedX = ((tx % max) + max) % max;
      tiles.push({
        // A fixed subdomain rather than {s}: browsers multiplex over HTTP/2, so
        // sharding buys nothing and costs an extra DNS lookup.
        url: `https://a.basemaps.cartocdn.com/rastertiles/voyager/${zoom}/${wrappedX}/${ty}@2x.png`,
        left: tx * TILE_SIZE - originX,
        top: ty * TILE_SIZE - originY,
      });
    }
  }

  return { tiles, pin: { left: width / 2, top: height / 2 }, width, height };
}
